import type { InputProps } from '@chakra-ui/react';
import { Group, Input, InputElement } from '@chakra-ui/react';
import * as React from 'react';

import {
  textFieldDefaultSize,
  textFieldIconContentGapPx,
  textFieldInputInlinePaddingPx,
  textFieldInputPaddingEndWithIcon,
  textFieldInputPaddingStartWithIcon,
  textFieldInputPaddingWithIconPx,
  type TextFieldSize,
} from './text-field.kds';

/** Interactive end-slot control — rendered as a focusable native button around `children`. */
export interface TextFieldEndElement {
  children: React.ReactNode;
  'aria-label': string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export type TextFieldEndElementProp = Exclude<React.ReactNode, undefined> | TextFieldEndElement;

const isInteractiveEndElement = (value: TextFieldEndElementProp): value is TextFieldEndElement =>
  typeof value === 'object' &&
  value !== null &&
  !React.isValidElement(value) &&
  'onClick' in value &&
  'children' in value &&
  'aria-label' in value;

export interface TextFieldProps extends Omit<InputProps, 'size'> {
  /** KDS size — `sm`, `md`, or `lg` per Figma. Default `sm`. */
  size?: TextFieldSize;
  startElement?: Exclude<React.ReactNode, undefined>;
  /**
   * Trailing slot. Pass a node for a decorative icon, or `{ children, 'aria-label', onClick }`
   * for a focusable control (tab + Enter/Space). Interactive mode uses `onPointerDown` +
   * `preventDefault` so activating the control does not steal focus from the input.
   */
  endElement?: TextFieldEndElementProp;
}

/**
 * Input with optional leading/trailing icon slots.
 *
 * Handles the `Group` / `InputElement` boilerplate and padding calculations
 * internally. Compose with `Field` for labels, errors, and helper text.
 */
export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function TextField(props, ref) {
  const { startElement, endElement, size = textFieldDefaultSize, disabled, ...inputProps } = props;

  let endContent: React.ReactNode;
  let interactiveEnd: TextFieldEndElement | undefined;
  if (endElement == null) {
    endContent = undefined;
    interactiveEnd = undefined;
  } else if (isInteractiveEndElement(endElement)) {
    interactiveEnd = endElement;
    endContent = endElement.children;
  } else {
    interactiveEnd = undefined;
    endContent = endElement;
  }
  const hasEndElement = endContent != null && endContent !== false;

  const input = (
    <Input
      ref={ref}
      size={size}
      variant="outline"
      disabled={disabled}
      ps={startElement ? textFieldInputPaddingStartWithIcon : undefined}
      pe={hasEndElement ? textFieldInputPaddingEndWithIcon : undefined}
      {...inputProps}
    />
  );

  if (!startElement && !hasEndElement) return input;

  const endSlot = interactiveEnd ? (
    <button
      type="button"
      aria-label={interactiveEnd['aria-label']}
      disabled={disabled}
      onClick={interactiveEnd.onClick}
      onPointerDown={event => {
        event.preventDefault();
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        color: 'inherit',
        cursor: disabled ? 'not-allowed' : 'pointer',
        lineHeight: 1,
      }}
    >
      {interactiveEnd.children}
    </button>
  ) : (
    endContent
  );

  return (
    <Group width="full">
      {startElement ? (
        <InputElement
          pointerEvents="none"
          color="neutral.250"
          width={`${textFieldInputPaddingWithIconPx}px`}
          ps={`${textFieldInputInlinePaddingPx}px`}
          pe={`${textFieldIconContentGapPx}px`}
          justifyContent="center"
          css={{ '& svg': { width: '20px', height: '20px' } }}
        >
          {startElement}
        </InputElement>
      ) : null}
      {input}
      {hasEndElement ? (
        <InputElement
          placement="end"
          color="neutral.250"
          width={`${textFieldInputPaddingWithIconPx}px`}
          pe={`${textFieldInputInlinePaddingPx}px`}
          ps={`${textFieldIconContentGapPx}px`}
          justifyContent="center"
          css={{ '& svg': { width: '20px', height: '20px' } }}
        >
          {endSlot}
        </InputElement>
      ) : null}
    </Group>
  );
});
